import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Avatar, Badge, Button, Card, Center, Group, Stack, Text, Title } from "@mantine/core";
import { useEffect } from "react";
import { IconLogin, IconLogout } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isInIframe, signIn, signOut } from "../config/msal";
import { useAppSelector } from "../store/hooks";
import { LoadingState } from "../components/ui";

export default function AccountPage() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { name, username, roles } = useAppSelector((s) => s.auth);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isLoading = inProgress !== InteractionStatus.None;
  const redirect = searchParams.get("redirect");

  useEffect(() => {
    if (!isAuthenticated || !redirect || isLoading) {
      return;
    }

    navigate(redirect, { replace: true });
  }, [isAuthenticated, isLoading, navigate, redirect]);

  const handleSignIn = async () => {
    // After login, land on the requested page or the home page (not /account).
    const target = redirect ?? "/";
    const completedInPage = await signIn(instance, target);
    if (completedInPage) navigate(target, { replace: true });
  };

  const handleSignOut = async () => {
    await signOut(instance);
    if (isInIframe()) navigate("/", { replace: true });
  };

  if (isLoading) {
    return <LoadingState h={400} />;
  }

  return (
    <Center h={400}>
      <Card shadow="sm" padding="xl" w={{ base: "100%", xs: 380 }}>
        {isAuthenticated ? (
          <Stack gap="md">
            <Group gap="md" wrap="nowrap">
              <Avatar color="brand" radius="md" size="lg">
                {(name ?? username ?? "?").slice(0, 1).toUpperCase()}
              </Avatar>
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Title order={3}>{name}</Title>
                <Text size="sm" c="dimmed" truncate>
                  {username}
                </Text>
              </Stack>
            </Group>

            {roles.length > 0 && (
              <Group gap="xs">
                {roles.map((role) => (
                  <Badge key={role} variant="light">
                    {role}
                  </Badge>
                ))}
              </Group>
            )}

            <Button leftSection={<IconLogout size={16} />} variant="light" color="red" onClick={handleSignOut}>
              Sign Out
            </Button>
          </Stack>
        ) : (
          <Stack align="center" gap="md">
            <Title order={3}>Sign In</Title>
            <Text c="dimmed" ta="center" size="sm">
              Sign in to access your content
            </Text>
            <Button leftSection={<IconLogin size={16} />} onClick={handleSignIn} fullWidth>
              Sign In
            </Button>
          </Stack>
        )}
      </Card>
    </Center>
  );
}
